// Referenced from jira/src/packages/platform/utils/fetch/src/utils/is-error.tsx
// Issues we are not gonna track in Sentry
const KNOWN_NETWORK_ERRORS_LIMITED = [
	'server with the specified hostname could not be found',
	'connection failure',
	'Failed to fetch',
	'Load failed', // Safari equivalent of "Failed to fetch"
	'Network request failed',
	'NetworkError',
	'The request timed out',
	'The network connection was lost',
	'The operation was aborted',
];

const isClientFetchErrorMessage = (message: string): boolean => {
	const errorTypes = KNOWN_NETWORK_ERRORS_LIMITED;
	return (
		Boolean(message) &&
		!!errorTypes.find((networkErrorMessage) =>
			// Using `includes` here because the underlying fetch error messages
			// are sometimes wrapped to provide additional context, e.g.
			// "Something went wrong: ${error.message}"
			message.includes(networkErrorMessage),
		)
	);
};

const KNOWN_NETWORK_ERRORS_TYPES = ['AbortError', 'AuthError', 'ChunkLoadError', 'NetworkError'];

export const isClientFetchError = (error: any): boolean =>
	Boolean(error) &&
	typeof error === 'object' &&
	(KNOWN_NETWORK_ERRORS_TYPES.includes(error.name) ||
		isClientFetchErrorMessage(error.message) ||
		(typeof error.cause === 'object' &&
			(KNOWN_NETWORK_ERRORS_TYPES.includes(error.cause.name) ||
				isClientFetchErrorMessage(error.cause.message))));

/**
 * Determines if an error is an auth or permission error (401/403).
 * These errors are caused by the user not being authenticated or not having
 * permission to use the feature (e.g. IP allowlist, HIPAA, product not licensed).
 * They should be excluded from SLO failure counts as they are not service reliability issues.
 */
export const isAuthOrPermissionError = (error: any): boolean => {
	if (!Boolean(error) || typeof error !== 'object') {
		return false;
	}
	// RovoInstrumentedError shape
	const statusFromAttributes = error?.errorAttributes?.statusCode;
	// Direct status on error
	const statusDirect = error?.status ?? error?.attributes?.status;
	const status = statusFromAttributes ?? statusDirect;
	return status === 401 || status === 403;
};

/**
 * Determines if an error is a "product not active" or "access denied" error.
 * These occur when Rovo is not enabled for the user's org/product, predominantly
 * in Jira. They should be excluded from SLO failure counts.
 *
 * Uses the structured errorCode from ApiError.errorAttributes rather than
 * string matching on the human-readable message field.
 */
export const isProductNotActiveError = (error: any): boolean => {
	if (!Boolean(error) || typeof error !== 'object') {
		return false;
	}
	// Check structured errorCode field (set by ApiError from the API response)
	const errorCode = (error?.errorAttributes?.errorCode ?? '').toLowerCase();
	return errorCode.includes('product.not.active') || errorCode.includes('product_not_active');
};
